// ═══ 카카오 공유 유틸 (정산서 전용, 2026-09-02 Jin 6차) ═══
// public/index.html 이 이미 Kakao JS SDK 2.7.2 를 로드하고 CSP 도 열려 있다.
// 앱 키는 utils/kakaoKey.js 가 hostname 별로 고른다 (도메인 ≠ 등록키 → Error 4019).
//
// ⚠️ 팝업 차단 규칙 — Kakao.Share.sendDefault 는 **클릭 핸들러에서 동기 호출**돼야 한다.
//    await 뒤(마이크로태스크 이후)에 부르면 사파리가 사용자 제스처 컨텍스트를 잃었다고 보고 팝업을 막는다.
//    그래서 이 모듈의 shareStatementLink 는 async 가 아니다. 호출부도
//    ① shareStatementLink(...) 먼저 → ② 서버 기록(sent)은 그 뒤 fire-and-forget 순서를 지킬 것.
//
// 카드에 금액을 절대 넣지 않는다 — 카톡 대화목록 미리보기에 청구액이 노출된다 (Jin 승인 조건).

import { getKakaoAppKey } from './kakaoKey';

// SDK 로드/초기화 확인 — 가능하면 true. init 은 1회만 (isInitialized 가 판정).
//
// ⚠️ 검사 순서 고정 — **init 이 먼저, Share 확인이 나중**.
//    카카오 JS SDK v2 는 로드 직후 window.Kakao 에 { VERSION, cleanup, init, isInitialized } 만 있고
//    Share/Auth/API/Channel/Navi/Picker 네임스페이스는 **init() 이 실행돼야 붙는다**
//    (2026-09-02 test.autodex.kr 실측: init 전 typeof Kakao.Share === 'undefined',
//     init 직후 'object' + sendDefault 'function').
//    init 전에 Share 를 먼저 보면 항상 false 가 되어 영원히 폴백으로 샌다 — 6차 결함의 원인.
//    (InvoiceSend.js 가 정상이던 이유도 같다: useEffect 에서 먼저 init 한다.)
export function ensureKakao() {
  if (typeof window === 'undefined') return false;
  if (window.__kakaoLoadFailed) return false;          // index.html onerror 가 세운 플래그
  var K = window.Kakao;
  if (!K || typeof K.init !== 'function') return false; // SDK 스크립트 자체가 없다
  try {
    if (typeof K.isInitialized === 'function' && !K.isInitialized()) {
      K.init(getKakaoAppKey((window.location && window.location.hostname) || ''));
    }
  } catch (e) {
    return false;                                       // 잘못된 키/중복 init 등
  }
  // init 이 끝난 뒤에야 Share 가 존재한다 — 여기서 판정.
  return !!(K.Share && typeof K.Share.sendDefault === 'function');
}

// 카드 배너 — 공유 링크와 **같은 오리진**의 /og/autodex-og.png.
// 그 경로는 server.js 의 express.static('/og') 가 서빙하고(1055), 정산서 공유 페이지가
// 스스로 OG 이미지로 선언하는 자산과 동일하다(routes/statements.js:520) → 카톡 카드와
// 링크 미리보기가 같은 그림이 된다. 클라에서 window.location 으로 오리진을 조립하지 않는다(#324).
var OG_FALLBACK = 'https://autodex.kr/og/autodex-og.png';
export function ogImageFor(url) {
  try {
    var u = new URL(String(url || ''));
    if (u.protocol === 'http:' || u.protocol === 'https:') return u.origin + '/og/autodex-og.png';
  } catch (e) { /* 잘못된 URL — 폴백 */ }
  return OG_FALLBACK;
}

// 정산서 공유 카드 전송. 동기 — 성공 true / SDK 불가·예외 false.
// onError(message) 로 호출부가 토스트를 띄운다 (도메인 미등록 4019 처럼 원인 파악이 필요한 예외).
export function shareStatementLink(opts) {
  var o = opts || {};
  var url = String(o.url || '');
  if (!url) return false;
  if (!ensureKakao()) return false;
  var link = { mobileWebUrl: url, webUrl: url };
  try {
    window.Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: String(o.title || '정산서'),
        description: String(o.description || 'AUTODEX 말소 대행 정산서'),
        imageUrl: ogImageFor(url),
        link: link
      },
      buttons: [{ title: '정산서 보기', link: link }]
    });
    return true;
  } catch (e) {
    // SDK 는 있는데 전송이 던진 경우 — 대표적으로 도메인 미등록(Error 4019). 조용히 삼키지 않는다.
    if (typeof o.onError === 'function') o.onError((e && e.message) || String(e));
    return false;
  }
}

// 말소증 공유 카드 전송 (W1, 2026-09-15). 정산서 카드와 같은 계약 — 동기 / 금액 없음.
export function shareMalsoLink(opts) {
  var o = opts || {};
  var url = String(o.url || '');
  if (!url) return false;
  if (!ensureKakao()) return false;
  var link = { mobileWebUrl: url, webUrl: url };
  try {
    window.Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: '말소증' + (o.carNo ? ' — ' + o.carNo : ''),
        description: 'AUTODEX 말소 대행 말소증',
        imageUrl: ogImageFor(url),
        link: link
      },
      buttons: [{ title: '말소증 보기', link: link }]
    });
    return true;
  } catch (e) {
    if (typeof o.onError === 'function') o.onError((e && e.message) || String(e));
    return false;
  }
}

// 'YYYY-MM' → 'YYYY년 M월' (카드 제목용 — 화면 표기와 같은 감각)
export function periodTitle(name, period) {
  var m = /^(\d{4})-(\d{2})$/.exec(String(period || ''));
  var p = m ? (m[1] + '년 ' + parseInt(m[2], 10) + '월') : String(period || '');
  return '[' + String(name || 'AUTODEX') + '] ' + (p ? p + ' ' : '') + '정산서';
}
